import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Percent, Loader2, Save, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const monthsAway = (expiryDate) => {
  const exp = new Date(expiryDate);
  const now = new Date();
  return (exp.getFullYear() - now.getFullYear()) * 12 + (exp.getMonth() - now.getMonth());
};

const monthLabel = (m) => (m === 0 ? 'this month' : m === 1 ? 'next month' : `within ${m} months`);

export const suggestDiscount = (expiryDate, tiers) => {
  const sorted = [...(tiers || [])].sort((a, b) => a.max_months - b.max_months);
  if (!sorted.length) return 20;
  if (!expiryDate) return sorted[sorted.length - 1].discount;
  const m = monthsAway(expiryDate);
  const hit = sorted.find(t => m <= t.max_months);
  return hit ? hit.discount : sorted[sorted.length - 1].discount;
};

export const ShortDatedRulesCard = ({ tiers, onSaved }) => {
  const { getAuthHeader } = useAuth();
  const [draft, setDraft] = useState(tiers.map(t => ({ ...t })));
  const [saving, setSaving] = useState(false);

  const update = (i, key, val) => setDraft(draft.map((t, idx) => idx === i ? { ...t, [key]: parseInt(val) || 0 } : t));
  const addRow = () => setDraft([...draft, { max_months: (draft[draft.length - 1]?.max_months ?? 0) + 1, discount: 10 }]);
  const removeRow = (i) => setDraft(draft.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/short-dated-stock/rules`, { tiers: draft }, { headers: getAuthHeader() });
      onSaved(res.data.tiers);
      toast.success('Discount rules saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save rules');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="short-dated-rules-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Percent className="w-4 h-4 text-yellow-500" /> Discount rules by expiry month
        </CardTitle>
        <CardDescription>When a product is scanned in, the discount comes from how many months away its expiry is. Anything later than the last row gets the last row's discount.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {draft.map((t, i) => (
          <div key={i} className="flex items-center gap-2 text-sm" data-testid={`sd-rule-row-${i}`}>
            <span>Expires</span>
            <Input type="number" min="0" className="w-16 h-8" value={t.max_months} onChange={e => update(i, 'max_months', e.target.value)} data-testid={`sd-rule-months-${i}`} />
            <span className="w-36 text-slate-500">months away ({monthLabel(t.max_months)}) →</span>
            <Input type="number" min="1" max="90" className="w-20 h-8" value={t.discount} onChange={e => update(i, 'discount', e.target.value)} data-testid={`sd-rule-discount-${i}`} />
            <span>% off</span>
            {draft.length > 1 && (
              <button type="button" onClick={() => removeRow(i)} className="text-slate-400 hover:text-rose-600" title="Remove row" data-testid={`sd-rule-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
        <div className="flex gap-2 mt-1">
          <Button size="sm" variant="outline" onClick={addRow} data-testid="sd-rules-add-row">
            <Plus className="w-4 h-4 mr-1" /> Add row
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-yellow-500 hover:bg-yellow-600" data-testid="sd-rules-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save rules
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const shortDatedLabel = (item, copies) => {
  const exp = item.expiry_date ? new Date(item.expiry_date) : null;
  const expTxt = exp ? `${String(exp.getMonth() + 1).padStart(2, '0')}/${String(exp.getFullYear()).slice(2)}` : '';
  return {
    name: item.product_name || item.name,
    barcode: item.product_barcode || item.barcode || '',
    sell_price: item.discounted_price,
    was_price: item.original_price,
    tag: `${item.discount_percent}% OFF${expTxt ? ` · EXP ${expTxt}` : ''}`,
    copies,
  };
};
